import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gogLink } from '../lib/affiliate';
import { getMultiStorePrices } from '../lib/api/pricesApi';

// Formatte une offre ITAD : "7,99 $" / "7,99 €".
function offerPrice(amount, currency) {
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  const cur = (currency || 'USD').toUpperCase();
  const sym = cur === 'EUR' ? '€' : cur === 'USD' ? '$' : cur;
  const val = n.toFixed(2).replace('.', ',');
  return cur === 'EUR' || cur === 'USD' ? `${val} ${sym}` : `${val} ${cur}`;
}

const JACKET = (appId) =>
  `https://cdn.cloudflare.steamstatic.com/steam/apps/${appId}/header.jpg`;
const JACKET_FALLBACK = (appId) =>
  `https://cdn.cloudflare.steamstatic.com/steam/apps/${appId}/capsule_231x87.jpg`;

// Certaines fiches Steam n'ont pas de header.jpg → on tente la capsule avant d'abandonner.
const onJacketError = (appId) => (e) => {
  const img = e.currentTarget;
  if (!img.dataset.fallback) {
    img.dataset.fallback = '1';
    img.src = JACKET_FALLBACK(appId);
  } else {
    img.style.visibility = 'hidden';
  }
};

// Prix GOG formaté (EUR → "9,99 €", sinon "9.99 USD").
function formatPrice(amount, currency) {
  if (amount == null) return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  if ((currency || 'EUR').toUpperCase() === 'EUR') {
    return `${n.toFixed(2).replace('.', ',')} €`;
  }
  return `${n.toFixed(2)} ${currency}`;
}

// Priorité de sauvegarde = gravité du risque × ampleur du temps de jeu.
// Plus tu y as joué et plus c'est menacé, plus il faut le racheter DRM-free en premier.
function priorityScore(item) {
  const sev = item.severity || 0;
  const hours = Math.round((item.playtimeMinutes || 0) / 60);
  return sev * (hours + 1);
}

function SaveList({ items, isPro, isDemo }) {
  const { t } = useTranslation();
  const [prices, setPrices] = useState({}); // appId -> { drmFree, gog, offers }

  // Uniquement les jeux à risque ET rachetables DRM-free, triés par priorité.
  const ranked = useMemo(
    () =>
      (items || [])
        .filter((i) => i.savable && i.gogUrl)
        .sort((a, b) => priorityScore(b) - priorityScore(a)),
    [items]
  );

  const appIdsKey = ranked.map((i) => i.appId).join(',');

  // Prix multi-store (ITAD) pour les jeux de la liste (Pro, ou aperçu démo).
  useEffect(() => {
    if (!(isPro || isDemo) || ranked.length === 0) return undefined;
    let alive = true;
    getMultiStorePrices(ranked.map((i) => i.appId))
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data || []).forEach((d) => {
          map[d.appId] = d;
        });
        setPrices(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, isDemo, appIdsKey]);

  // Statut Pro en cours de chargement : on ne rend rien pour éviter le flash.
  if (isPro === null) return null;

  // Non-Pro ET hors démo : upsell verrouillé. (En démo, on montre un aperçu de la liste.)
  if (!isPro && !isDemo) {
    return (
      <section className="savelist savelist--locked">
        <span className="savelist__lock" aria-hidden="true">🔒</span>
        <h2 className="savelist__title">{t('savelist.title')}</h2>
        <p className="savelist__subtitle">{t('savelist.locked')}</p>

        <div className="savelist__explain">
          <span className="savelist__explain-title">{t('savelist.explain_title')}</span>
          <p className="savelist__explain-body">{t('savelist.explain_body')}</p>
          <span className="savelist__explain-formula">{t('savelist.explain_formula')}</span>
        </div>

        <a className="btn btn--amber" href="/tarifs">{t('savelist.go_pro')}</a>
      </section>
    );
  }

  // Pro mais rien à sauver.
  if (ranked.length === 0) {
    return (
      <section className="savelist">
        <span className="savelist__pro-badge">{isPro ? 'PRO ✓' : t('savelist.preview')}</span>
        <h2 className="savelist__title">{t('savelist.title')}</h2>
        <p className="savelist__empty">{t('savelist.empty')}</p>
      </section>
    );
  }

  return (
    <section className="savelist">
      <span className="savelist__pro-badge">{isPro ? 'PRO ✓' : t('savelist.preview')}</span>
      <h2 className="savelist__title">{t('savelist.title')}</h2>
      <p className="savelist__subtitle">{t('savelist.subtitle')}</p>
      {!isPro && <p className="savelist__demo-note">🔒 {t('savelist.demo_pro_note')}</p>}

      <ol className="savelist__list">
        {ranked.map((i, idx) => {
          const hours = Math.round((i.playtimeMinutes || 0) / 60);
          const price = formatPrice(i.gogPrice, i.gogCurrency);
          return (
            <li key={i.appId} className="savelist__row">
              <span className="savelist__rank">#{idx + 1}</span>
              <img
                className="savelist__jacket"
                src={JACKET(i.appId)}
                alt=""
                loading="lazy"
                onError={onJacketError(i.appId)}
              />
              <div className="savelist__info">
                <span className="savelist__name">{i.name}</span>
                <span className="savelist__meta">
                  <span className={`savelist__risk savelist__risk--${i.riskType}`}>
                    {t(`risk.${i.riskType}`)}
                  </span>
                  {hours > 0 && (
                    <span className="savelist__hours">
                      {t('savelist.playtime', { hours })}
                    </span>
                  )}
                </span>
                {price && (
                  <span className="savelist__price">
                    <span className="savelist__price-tag">◆ GOG</span>
                    {price}
                    {i.gogDiscount > 0 && (
                      <span className="savelist__discount">-{i.gogDiscount}%</span>
                    )}
                  </span>
                )}
                {(() => {
                  // "Aussi dispo" = uniquement les boutiques AVEC DRM (le sans-DRM = GOG,
                  // déjà mis en avant par le bouton). 3 max, moins cher d'abord.
                  const alt = (prices[i.appId]?.offers || [])
                    .filter((o) => !o.drmFree)
                    .slice(0, 3);
                  if (alt.length === 0) return null;
                  // Comparaison multi-boutiques = avantage Pro. En démo (non-Pro), on l'affiche
                  // mais NON cliquable, avec un cadenas, pour donner envie de passer Pro.
                  const locked = !isPro;
                  return (
                    <span className={`savelist__stores ${locked ? 'savelist__stores--locked' : ''}`}>
                      <span className="savelist__stores-label">
                        {t('savelist.also_on')}
                        {locked && <span className="savelist__stores-lock"> 🔒 {t('savelist.pro_locked')}</span>}
                      </span>
                      <span className="savelist__stores-grid">
                        {alt.map((o, k) => {
                          const label = (
                            <>
                              {o.shop} {offerPrice(o.price, o.currency)}
                              {o.cut > 0 && <em className="savelist__store-cut">-{o.cut}%</em>}
                            </>
                          );
                          return locked ? (
                            <span
                              key={`${o.shop}-${k}`}
                              className="savelist__store savelist__store--locked"
                              title={t('savelist.pro_locked')}
                              aria-disabled="true"
                            >
                              {label}
                            </span>
                          ) : (
                            <a
                              key={`${o.shop}-${k}`}
                              className="savelist__store"
                              href={o.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('savelist.withdrm')}
                            >
                              {label}
                            </a>
                          );
                        })}
                      </span>
                    </span>
                  );
                })()}
              </div>
              <a
                className="btn btn--mint savelist__cta"
                href={gogLink(i.gogUrl)}
                target="_blank"
                rel="noopener noreferrer"
                title={t('savelist.drmfree')}
              >
                <span className="savelist__cta-main">{t('savelist.buy')}</span>
                <span className="savelist__cta-sub">◆ {t('savelist.drmfree_short')}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default SaveList;
